import React, { useState, useEffect } from 'react';
import Layout from '../Layout';
import {
  Calendar,
  Clock,
  AlertCircle,
  CheckCircle,
  Filter,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  User,
  MapPin,
  BookOpen
} from 'lucide-react';
import './StudentCalendar.css';
import { API_CONFIG } from '../config/api';

const StudentCalendar = () => {
  // State management
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('calendar'); // calendar, events
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data state
  const [events, setEvents] = useState([]);
  const [calendarData, setCalendarData] = useState({});
  const [stats, setStats] = useState({
    total_events: 0,
    upcoming_events: 0,
    today_events: 0,
    this_week_events: 0
  });

  // Filter state
  const [filters, setFilters] = useState({
    eventType: ''
  });

  // API base URL
  const API_BASE_URL = API_CONFIG.BASE_URL;

  // Fetch data on component mount
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch calendar data when date changes
  useEffect(() => {
    fetchCalendarData();
  }, [currentDate]);

  // API helper function
  const apiCall = async (endpoint, options = {}) => {
    const token = localStorage.getItem('accessToken');
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      },
      ...options
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}`);
    }

    return response.json();
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const statsData = await apiCall('/calendar/student-stats');
      setStats(statsData);
    } catch (err) {
      setError(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch calendar data for current month
  const fetchCalendarData = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;

      const data = await apiCall(`/calendar/student-calendar-data?year=${year}&month=${month}`);
      setCalendarData(data.calendar_data || {});
    } catch (err) {
      console.error('Failed to fetch calendar data:', err);
    }
  };

  // Fetch events with filters
  const fetchEvents = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) {
          params.append(key, value);
        }
      });

      const data = await apiCall(`/calendar/student-events?${params.toString()}`);
      setEvents(data.events || []);
    } catch (err) {
      setError(`Failed to fetch events: ${err.message}`);
    }
  };

  // Calendar navigation
  const previousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() - 1);
      return newDate;
    });
  };

  const nextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + 1);
      return newDate;
    });
  };

  // Utility functions
  const getCategoryIcon = (category) => {
    const icons = {
      academic: '📚',
      assignment: '📝',
      exam: '📊',
      meeting: '💼',
      event: '🎉',
      reminder: '⏰'
    };
    return icons[category] || '📅';
  };

  const getEventTypeColor = (eventType) => {
    const colors = {
      academic: 'bg-blue-100 text-blue-800 border-blue-200',
      assignment: 'bg-orange-100 text-orange-800 border-orange-200',
      exam: 'bg-red-100 text-red-800 border-red-200',
      meeting: 'bg-purple-100 text-purple-800 border-purple-200',
      event: 'bg-green-100 text-green-800 border-green-200',
      reminder: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return colors[eventType] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Render calendar grid
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    // Empty cells for days before the first day of the month
    const emptyCells = Array(firstDay).fill(null).map((_, i) => (
      <div key={`empty-${i}`} className="h-24 border border-gray-100"></div>
    ));

    // Generate days
    const days = Array(daysInMonth).fill(null).map((_, i) => {
      const day = i + 1;
      const currentDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendarData[currentDateStr] || { events: [] };
      const today = new Date().toISOString().split('T')[0];
      const isToday = currentDateStr === today;

      return (
        <div
          key={`day-${day}`}
          className={`h-24 border border-gray-100 p-2 rounded-lg transition-colors ${isToday ? 'bg-blue-100 border-blue-300' : 'bg-white hover:bg-gray-50'
            }`}
        >
          <div className="font-semibold text-gray-800 mb-1">{day}</div>
          <div className="space-y-1">
            {dayData.events.slice(0, 2).map((event, idx) => (
              <div
                key={`event-${idx}`}
                className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800 truncate"
                style={{ backgroundColor: event.color + '20', color: event.color }}
              >
                {getCategoryIcon(event.event_type)} {event.title}
              </div>
            ))}
            {dayData.events.length > 2 && (
              <div className="text-xs text-gray-500">
                +{dayData.events.length - 2} more
              </div>
            )}
          </div>
        </div>
      );
    });

    return (
      <>
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={previousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-gray-600" />
            </button>
            <h3 className="text-2xl font-bold text-gray-800 min-w-[200px] text-center">
              {monthName}
            </h3>
            <button
              onClick={nextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2 mb-4">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
            <div key={`header-${i}`} className="text-center font-semibold text-gray-600 py-3 bg-gray-50 rounded-lg">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-2">
          {emptyCells}
          {days}
        </div>
      </>
    );
  };

  // Render stats cards
  const renderStats = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Events</p>
              <p className="text-3xl font-bold text-blue-600 mt-1">{stats.total_events || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-100">
              <Calendar className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Upcoming Events</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{stats.upcoming_events || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-green-100">
              <Clock className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Today's Events</p>
              <p className="text-3xl font-bold text-orange-600 mt-1">{stats.today_events || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-orange-100">
              <CheckCircle className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200 transition-all duration-300 hover:shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">This Week</p>
              <p className="text-3xl font-bold text-purple-600 mt-1">{stats.this_week_events || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-purple-100">
              <BookOpen className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <div className="bg-white font-[poppins] min-h-full w-full relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">

          {/* Notifications */}
          {error && (
            <div className="fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center gap-3 transition-all duration-300 bg-red-500 text-white" style={{ animation: 'slideInRight 0.5s ease-out' }}>
              <AlertCircle className="w-5 h-5" />
              <span className="font-[poppins]">{error}</span>
              <button onClick={() => setError(null)} className="ml-2 hover:opacity-80">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Header */}
          <div className="pt-8 pb-8 text-center relative overflow-hidden">
            <div className="relative z-10">
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6" style={{ backgroundColor: '#D67C2C' }}>
                  <Calendar className="w-8 h-8 text-white" />
                </div>
                <h1 className="text-5xl font-bold bg-[#D67C2C] bg-clip-text text-transparent mb-4">
                  My Calendar
                </h1>
                <p className="text-[#1D3469] text-xl font-medium max-w-3xl mx-auto px-4">
                  View your scheduled events and activities
                </p>
              </div>
            </div>
          </div>

          {/* Dashboard Cards */}
          {renderStats()}

          {/* View Toggle */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 mb-8">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Filter className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">Calendar Views</h2>
                </div>

                <div className="flex bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setView('calendar')}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${view === 'calendar'
                        ? 'bg-[#D67C2C] text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                      }`}
                  >
                    Calendar View
                  </button>
                  <button
                    onClick={() => {
                      setView('events');
                      fetchEvents();
                    }}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 ${view === 'events'
                        ? 'bg-[#D67C2C] text-white shadow-md'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
                      }`}
                  >
                    Events List
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          {view === 'calendar' && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                    <Calendar className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">My Events Calendar</h2>
                </div>
              </div>
              <div className="p-6">
                {renderCalendar()}
              </div>
            </div>
          )}

          {view === 'events' && (
            <div className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 bg-gray-50">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#D67C2C]">
                      <Calendar className="w-5 h-5 text-white" />
                    </div>
                    <h2 className="text-xl font-semibold text-gray-900 font-[poppins]">My Events</h2>
                  </div>

                  <div className="flex space-x-3">
                    <select
                      value={filters.eventType}
                      onChange={(e) => setFilters(prev => ({ ...prev, eventType: e.target.value }))}
                      className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D67C2C] focus:border-transparent"
                    >
                      <option value="">All Types</option>
                      <option value="academic">Academic</option>
                      <option value="assignment">Assignment</option>
                      <option value="exam">Exam</option>
                      <option value="meeting">Meeting</option>
                      <option value="event">Event</option>
                      <option value="reminder">Reminder</option>
                    </select>
                    <button
                      onClick={fetchEvents}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <Filter className="w-4 h-4" />
                      Filter
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {events.length === 0 ? (
                  <div className="flex flex-col justify-center items-center p-12 text-gray-500">
                    <Calendar className="w-16 h-16 mb-4 text-gray-300" />
                    <p className="text-lg mb-3">No events scheduled</p>
                    <p className="text-sm text-gray-400 mb-6">
                      {filters.eventType ? "Try clearing your filter to see all events." : "You don't have any events scheduled yet."}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {events.map(event => (
                      <div key={event._id} className="border border-gray-200 rounded-xl p-6 hover:shadow-md transition-all duration-300 bg-white">
                        <div className="flex justify-between items-start mb-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex-shrink-0 h-10 w-10 bg-[#D67C2C] bg-opacity-10 rounded-full flex items-center justify-center">
                                <span className="text-lg">{getCategoryIcon(event.event_type)}</span>
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-800 text-lg">{event.title}</h3>
                                <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getEventTypeColor(event.event_type)}`}>
                                  {event.event_type.charAt(0).toUpperCase() + event.event_type.slice(1)}
                                </span>
                              </div>
                            </div>
                            <p className="text-gray-600 mb-3">{event.description || 'No description'}</p>

                            <div className="flex items-center space-x-6 text-sm text-gray-500">
                              <div className="flex items-center space-x-2">
                                <Clock className="w-4 h-4" />
                                <span>{new Date(event.start_date).toLocaleDateString()}</span>
                                {!event.is_all_day && (
                                  <span className="text-gray-400">
                                    at {new Date(event.start_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </div>
                              {event.location && (
                                <div className="flex items-center space-x-2">
                                  <MapPin className="w-4 h-4" />
                                  <span>{event.location}</span>
                                </div>
                              )}
                              {event.teacher_name && (
                                <div className="flex items-center space-x-2">
                                  <User className="w-4 h-4" />
                                  <span>By: {event.teacher_name}</span>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex flex-col items-end space-y-2 ml-4">
                            <div
                              className="w-4 h-4 rounded-full border-2 border-white shadow-sm"
                              style={{ backgroundColor: event.color }}
                            />
                            {event.is_all_day && (
                              <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                                All Day
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Event details footer */}
                        <div className="pt-4 border-t border-gray-100">
                          <div className="flex justify-between items-center text-sm text-gray-500">
                            <span>Created: {new Date(event.created_at).toLocaleDateString()}</span>
                            {event.updated_at && event.updated_at !== event.created_at && (
                              <span>Updated: {new Date(event.updated_at).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StudentCalendar;